// Bară de FILTRE GLOBALE — sticky sub header. Conduce toate getterele derivate.
//
// Perioadă: presetări (ultimele 1/3/6/12 luni, tot) + interval custom (2 dropdown-uri lună).
// Dimensiuni: client, categorie produs, segment, mașină — se aplică pe rapoartele
// cu grain comandă (profitabilitate, top clienți, mix, livrări, export) și, unde
// are sens, pe încărcarea mașinilor. Secțiunile pur temporale (cost, media mobilă)
// răspund doar la perioadă.

import { Select, SelectItem } from "@tremor/react";
import { Filter, RotateCcw, CalendarRange } from "lucide-react";
import {
  useFilters,
  setPreset,
  setFilter,
  setCustomRange,
  resetFilters,
  activeFilterCount,
  type PerioadaPreset,
} from "@/data/filters";
import { allMonths, activeWindow, distinctClienti, distinctCategorii, distinctMasini } from "@/data/derived";
import { formatLunaKey } from "@/lib/format";
import { labelCategorie } from "@/lib/labels";
import { cn } from "@/lib/utils";

const PRESETS: Array<{ key: PerioadaPreset; label: string }> = [
  { key: "1", label: "1L" },
  { key: "3", label: "3L" },
  { key: "6", label: "6L" },
  { key: "12", label: "12L" },
  { key: "all", label: "Tot" },
];

export function FilterBar() {
  const f = useFilters();
  const months = allMonths();
  const win = activeWindow();
  const clienti = distinctClienti();
  const categorii = distinctCategorii();
  const masini = distinctMasini();
  const nrActive = activeFilterCount(f);

  return (
    <div className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur lg:top-0">
      <div className="mx-auto max-w-[1440px] px-4 py-2.5 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
            <Filter className="h-4 w-4 text-brand-600" />
            Filtre
            {nrActive > 0 && (
              <span className="ml-0.5 inline-flex items-center rounded-full bg-brand-100 px-1.5 py-0.5 text-[11px] font-semibold text-brand-700">
                {nrActive} active
              </span>
            )}
          </div>

          {/* Presetări perioadă */}
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                className={cn(
                  "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                  f.preset === p.key
                    ? "bg-brand-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-white hover:text-slate-900",
                )}
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={() =>
                setCustomRange(f.customFrom ?? months[0] ?? null, f.customTo ?? months[months.length - 1] ?? null)
              }
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors",
                f.preset === "custom"
                  ? "bg-brand-600 text-white shadow-sm"
                  : "text-slate-600 hover:bg-white hover:text-slate-900",
              )}
            >
              <CalendarRange className="h-3.5 w-3.5" />
              Interval
            </button>
          </div>

          {/* Interval custom */}
          {f.preset === "custom" && (
            <div className="flex items-center gap-1.5">
              <Select
                value={f.customFrom ?? months[0]}
                onValueChange={(v) => setCustomRange(v, f.customTo ?? months[months.length - 1])}
                enableClear={false}
                className="w-32"
              >
                {months.map((m) => (
                  <SelectItem key={m} value={m}>
                    {formatLunaKey(m)}
                  </SelectItem>
                ))}
              </Select>
              <span className="text-xs text-slate-400">→</span>
              <Select
                value={f.customTo ?? months[months.length - 1]}
                onValueChange={(v) => setCustomRange(f.customFrom ?? months[0], v)}
                enableClear={false}
                className="w-32"
              >
                {months.map((m) => (
                  <SelectItem key={m} value={m}>
                    {formatLunaKey(m)}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )}

          {/* Dimensiuni */}
          <Select
            value={f.client}
            onValueChange={(v) => setFilter("client", v)}
            enableClear={false}
            className="w-full sm:w-48"
            placeholder="Client"
          >
            <SelectItem value="all">Toți clienții</SelectItem>
            {clienti.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </Select>

          <Select
            value={f.categorie}
            onValueChange={(v) => setFilter("categorie", v)}
            enableClear={false}
            className="w-full sm:w-44"
            placeholder="Categorie"
          >
            <SelectItem value="all">Toate categoriile</SelectItem>
            {categorii.map((c) => (
              <SelectItem key={c} value={c}>
                {labelCategorie(c)}
              </SelectItem>
            ))}
          </Select>

          <Select
            value={f.segment}
            onValueChange={(v) => setFilter("segment", v as "all" | "export" | "intern")}
            enableClear={false}
            className="w-full sm:w-36"
            placeholder="Segment"
          >
            <SelectItem value="all">Export + Intern</SelectItem>
            <SelectItem value="export">Doar export</SelectItem>
            <SelectItem value="intern">Doar intern</SelectItem>
          </Select>

          <Select
            value={f.masina}
            onValueChange={(v) => setFilter("masina", v)}
            enableClear={false}
            className="w-full sm:w-48"
            placeholder="Mașină"
          >
            <SelectItem value="all">Toate mașinile</SelectItem>
            {masini.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </Select>

          {nrActive > 0 && (
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Resetează
            </button>
          )}

          <span className="ml-auto hidden shrink-0 text-xs text-slate-400 lg:inline">
            Perioadă: {formatLunaKey(win.from)} – {formatLunaKey(win.to)}
          </span>
        </div>
      </div>
    </div>
  );
}
